import React, { useEffect, useState } from "react";
import bannerImage from "../assets/contactus.jpg";
import "../styles/ContactUs.css";

// Import your API service
import { submitContactUs } from "../services/apiService";

const ContactUs = ({ showImages = true }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // CAPTCHA states
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  // Generate CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 9) + 1;
    const num2 = Math.floor(Math.random() * 9) + 1;

    setCaptchaQuestion(`${num1} + ${num2} = ?`);
    setCaptchaAnswer(String(num1 + num2));
    setCaptchaInput("");
    setCaptchaError("");
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrorMessage("");
  };

  const handleCaptchaChange = (e) => {
    setCaptchaInput(e.target.value);
    setCaptchaError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage("");
    setCaptchaError("");

    // Validate CAPTCHA
    if (captchaInput.trim() !== captchaAnswer) {
      setCaptchaError("Incorrect CAPTCHA. Please try again.");
      generateCaptcha();
      return;
    }

    setIsLoading(true);

    try {
      // API payload
      const payload = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      };

      console.log("Contact API Payload:", payload);

      // Call API
      const response = await submitContactUs(payload);

      console.log("Contact API Response:", response);

      // Success
      setIsSubmitted(true);

      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      // Generate new CAPTCHA
      generateCaptcha();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error("Contact API Error:", error);

      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Something went wrong. Please try again."
      );

      // Generate new CAPTCHA
      generateCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  // Scroll animation observer
  useEffect(() => {
    const elements = document.querySelectorAll(".animate-on-scroll");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animated");
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="contact-us-container">
      <div className="contact-inner-container">

        <header className="contact-header">
          {showImages && (
            <section
              className="hero-section animate-on-scroll"
              style={{
                backgroundImage: `url(${bannerImage})`,
              }}
            ></section>
          )}
        </header>

        <main className="contact-main">
          <div className="contact-content-container">

            <div className="contact-wrapper">

              {/* LEFT SIDE */}
              <div className="contact-info">
                <h2>Get In Touch</h2>

                <p>
                  Feel free to reach out to us with any questions, comments,
                  or concerns. Our team is here to help and will respond as
                  quickly as possible.
                </p>

                <div className="info-item">
                  <div className="info-text">
                    <h3>Email Address</h3>
                    <p>info@pixeltools.in</p>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-text">
                    <h3>Phone Number</h3>
                    <p>+91 9100039893</p>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-text">
                    <h3>Office Location</h3>

                    <p>
                      Ficci Flo Industrial Park, F4
                      <br />
                      Ameenpur Mandal, Hyderabad
                      <br />
                      Sangareddy
                      <br />
                      Telangana, State Code: 36
                      <br />
                      PIN Code: 502319
                      <br />
                      India
                    </p>
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE */}
              <div className="contact-form-container">

                <div className="form-header">
                  <h2>Contact Us</h2>

                  <p>
                    Fill out the form below and we'll get back to you shortly.
                  </p>
                </div>

                {/* SUCCESS MESSAGE */}
                {isSubmitted ? (
                  <div className="success-message">
                    <i className="fas fa-check-circle"></i>

                    <h3>Message Sent Successfully!</h3>

                    <p>
                      Thank you for contacting us. We'll respond as soon as
                      possible.
                    </p>
                  </div>
                ) : (
                  <form
                    className="contact-form"
                    onSubmit={handleSubmit}
                  >

                    {/* NAME + EMAIL */}
                    <div className="form-row">

                      <div className="form-group">
                        <label htmlFor="name">
                          Your Name
                        </label>

                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          required
                          disabled={isLoading}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">
                          Your Email
                        </label>

                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          required
                          disabled={isLoading}
                        />
                      </div>

                    </div>

                    {/* SUBJECT */}
                    <div className="form-group">
                      <label htmlFor="subject">
                        Subject
                      </label>

                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Enter the subject"
                        required
                        disabled={isLoading}
                      />
                    </div>

                    {/* MESSAGE */}
                    <div className="form-group">
                      <label htmlFor="message">
                        Message
                      </label>

                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Enter your message"
                        rows="6"
                        required
                        disabled={isLoading}
                      />
                    </div>

                    {/* CAPTCHA */}
                    <div className="captcha-container">

                      <label htmlFor="captcha">
                        Security Verification
                      </label>

                      <div className="captcha-row">

                        <div className="captcha-question">
                          {captchaQuestion}
                        </div>

                        <button
                          type="button"
                          className="captcha-refresh"
                          onClick={generateCaptcha}
                          disabled={isLoading}
                          title="Refresh CAPTCHA"
                        >
                          ↻
                        </button>

                      </div>

                   <div className="captcha-input-wrapper">
  <span className="captcha-input-icon">✓</span>

  <input
    type="text"
    id="captcha"
    value={captchaInput}
    onChange={handleCaptchaChange}
    placeholder="Enter CAPTCHA answer"
    autoComplete="off"
    required
    disabled={isLoading}
  />
</div>

                      {captchaError && (
                        <p className="captcha-error">
                          {captchaError}
                        </p>
                      )}

                    </div>

                    {/* API ERROR */}
                    {errorMessage && (
                      <div className="form-error">
                        {errorMessage}
                      </div>
                    )}

                    {/* SUBMIT */}
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <i className="fas fa-paper-plane"></i>
                        </>
                      )}
                    </button>

                  </form>
                )}

              </div>

            </div>

          </div>
        </main>

      </div>
    </div>
  );
};

export default ContactUs;